"use client";

import { memo, useState, useCallback, useMemo } from 'react';
import styles from './FilterSection.module.scss';
import ExpressDeliveryFilter from '@components/atoms/ExpressDeliveryFilter';
import FilterGroups from '@components/molecules/FilterGroups';
import Text from '@components/atoms/Text';
import { useFilter } from '@context/FilterContext';
import MobileFilterModal from '@components/molecules/MobileFilterModal';
import FilterChips from '@components/atoms/FilterChips';
import { FilterCategory, BudgetRange } from '@interfaces/filter.types';
import Button from '@components/atoms/Button';
import { usePathname } from 'next/navigation';
import { filterHelpers } from '@helpers/filterLogic';
import { createUnifiedFilterConfig } from '@helpers/filterConfigHelpers';
import { trackEventWithCommonProps, EventType } from '@utils/eventTracker';
import { PAGE_TYPES } from '@constants/pageTypes';

const FilterSection = memo(({ cityName, collectionName, variant = 'default' }: { cityName: string, collectionName: string, variant?: 'default' | 'app' }) => {
  const { state, dispatch, filterOptions, isInitialized } = useFilter();
  const pathname = usePathname();
  const pageName = pathname?.includes("collection")
    ? PAGE_TYPES.COLLECTION_PAGE
    : PAGE_TYPES.BUY_HOMEPAGE; 

  const config = useMemo(() => createUnifiedFilterConfig(filterOptions), [filterOptions]);

  const desktopFilterMetrics = useMemo(() => {
    if (!filterOptions) return { hasAnyActiveFilters: false, hasAppliedFilters: false };

    const budgetDefaults = {
      min: filterOptions?.budget?.default_min_price || 0,
      max: filterOptions?.budget?.default_max_price || 0
    };

    const stateWithoutExpressDelivery = {
      ...state,
      is_express_delivery: false
    };

    const hasTempSelections = filterHelpers.getActiveFiltersCount(stateWithoutExpressDelivery, budgetDefaults) > 0;
    
    const hasAppliedFilters = state.appliedFilters && Object.entries(state.appliedFilters).some(([key, value]) => {
      if (key === 'is_express_delivery') return false;
      if (key === 'budget') {
        const budgetValue = value as { min: number; max: number } | undefined;
        return budgetValue && (budgetValue.min !== budgetDefaults.min || budgetValue.max !== budgetDefaults.max);
      }
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });

    return {
      hasAnyActiveFilters: hasTempSelections || hasAppliedFilters,
      hasAppliedFilters
    };
  }, [state, filterOptions]);

  const mobileFilterMetrics = useMemo(() => {
    if (!filterOptions) return { hasAnyActiveFilters: false, hasAppliedFilters: false };

    const budgetDefaults = {
      min: filterOptions?.budget?.default_min_price || 0,
      max: filterOptions?.budget?.default_max_price || 0
    };

    const hasTempSelections = filterHelpers.getActiveFiltersCount(state, budgetDefaults) > 0;
    
    const hasAppliedFilters = state.appliedFilters && Object.entries(state.appliedFilters).some(([key, value]) => {
      if (key === 'budget') {
        const budgetValue = value as { min: number; max: number } | undefined;
        return budgetValue && (budgetValue.min !== budgetDefaults.min || budgetValue.max !== budgetDefaults.max);
      }
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });

    return {
      hasAnyActiveFilters: hasTempSelections || hasAppliedFilters,
      hasAppliedFilters
    };
  }, [state, filterOptions]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(config.initialOpenState);
  const [activeFilterType, setActiveFilterType] = useState<string | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);

  const isExpressDeliveryCollection = useMemo(() =>
    filterHelpers.isExpressDeliveryCollection(pathname || ""),
    [pathname]
  );

  const handleFilterChange = useCallback(async (category: FilterCategory, values: string[]) => {
    dispatch({ type: 'SET_FILTER', category, values });
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter",
        filter_type: category,
        selection: values,
        city_name: cityName,
        collection_name: collectionName,
      },
    });
  }, [dispatch]);

  const handleBudgetChange = useCallback(async (values: BudgetRange) => {
    dispatch({ type: 'SET_BUDGET', values });
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter",
        filter_type: "budget",
        selection: values,
        city_name: cityName,
        collection_name: collectionName,
      },
    });
  }, [dispatch]);

  const handleExpressDeliveryChange = useCallback(async (value: boolean) => {
    dispatch({ type: 'APPLY_EXPRESS_DELIVERY_FILTER', value });
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter_apply",
        selection: "express_delivery",
        city_name: cityName,
        collection_name: collectionName,
      },
    });
  }, [dispatch]);

  const handleMobileExpressDeliveryChange = useCallback(async (value: boolean) => {
    dispatch({ type: 'SET_EXPRESS_DELIVERY', value });
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter_apply",
        selection: "express_delivery",
        city_name: cityName,
        collection_name: collectionName,
      },
    });
  }, [dispatch]);

  const handleClearAll = useCallback(() => {
    if (!desktopFilterMetrics.hasAnyActiveFilters) return;
    dispatch({ type: 'CLEAR_ALL_FILTERS' });
    setActiveFilterType(null);

    const allClosedSections = Object.keys(openSections).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {} as Record<string, boolean>);
    setOpenSections(allClosedSections);
  }, [dispatch, desktopFilterMetrics.hasAnyActiveFilters, openSections]);

  const handleMobileClearAll = useCallback(() => {
    if (!mobileFilterMetrics.hasAnyActiveFilters) return;
    dispatch({ type: 'CLEAR_ALL_FILTERS' });
    setActiveFilterType(null);
    setShowMobileFilters(false);
  }, [dispatch, mobileFilterMetrics.hasAnyActiveFilters]);

  const getSelectedFilters = useCallback(() => {
    if (!filterOptions) return {};

    const budgetDefaults = {
      min: filterOptions?.budget?.default_min_price || 0,
      max: filterOptions?.budget?.default_max_price || 0
    };

    const selectedFilters: any = {};

    if (state.body_type?.length > 0) selectedFilters.body_type = state.body_type;
    if (state.brand_model?.length > 0) selectedFilters.brand_model = state.brand_model;
    if (state.fuel_type?.length > 0) selectedFilters.fuel_type = state.fuel_type;
    if (state.transmission?.length > 0) selectedFilters.transmission = state.transmission;
    if (state.feature?.length > 0) selectedFilters.feature = state.feature;
    if (state.sort_by?.length > 0) selectedFilters.sort_by = state.sort_by;
    if (state.seat_capacity?.length > 0) selectedFilters.seat_capacity = state.seat_capacity;
    
    if (state.budget.min !== budgetDefaults.min || state.budget.max !== budgetDefaults.max) {
      selectedFilters.budget = state.budget;
    }
    
    if (state.is_express_delivery) {
      selectedFilters.is_express_delivery = true;
    }

    return selectedFilters;
  }, [state, filterOptions]);

  const handleApplyFilters = useCallback(async () => {
    if (!desktopFilterMetrics.hasAnyActiveFilters) return;
    
    const selectedFilters = getSelectedFilters();
    
    dispatch({ type: 'APPLY_FILTERS' });
    setActiveFilterType(null);
    setShowMobileFilters(false);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter_apply",
        selection: selectedFilters,
        city_name: cityName,
        collection_name: collectionName,
      },
    });
  }, [dispatch, desktopFilterMetrics.hasAnyActiveFilters, getSelectedFilters, pageName, cityName, collectionName]);


  const handleMobileApplyFilters = useCallback(async () => {
    if (!mobileFilterMetrics.hasAnyActiveFilters) return;
    
    const selectedFilters = getSelectedFilters();
    
    dispatch({ type: 'APPLY_FILTERS' });
    setActiveFilterType(null);
    setShowMobileFilters(false);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter_apply",
        selection: selectedFilters,
        city_name: cityName,
        collection_name: collectionName,
      },
    });
  }, [dispatch, mobileFilterMetrics.hasAnyActiveFilters, getSelectedFilters, pageName, cityName, collectionName]);

  const handleToggleSection = useCallback((section: string) => {
    const isOpening = !openSections[section];
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));

    if (isOpening && config.categories.length > 0) {
      const lastFilterKey = config.categories[config.categories.length - 1]?.key;
      if (section === lastFilterKey) {
        setTimeout(() => {
          const filterContainer = document.querySelector(`.${styles.filterContent}`);
          if (filterContainer) {
            filterContainer.scrollTo({
              top: filterContainer.scrollHeight,
              behavior: 'smooth'
            });
          }
        }, 150);
      }
    }
  }, [openSections, config.categories, styles.filterContent]);

  const handleMobileFilterClick = useCallback(async (filterType: string) => {
    setActiveFilterType(filterType);
    setShowMobileFilters(true);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter",
        selection: activeFilterType,
        city_name: cityName,
        collection_name: collectionName,
      },
    });
  }, []);

  const handleMobileFilterButtonClick = useCallback(async () => {
    setShowMobileFilters(true);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter",
        selection: "opened_mobile_filter",
        city_name: cityName,
        collection_name: collectionName,
      },
    });
  }, []);

  const handleCloseMobileFilter = useCallback(() => {
    setActiveFilterType(null);
    setShowMobileFilters(false);
  }, []);

  const hasFilterGroups = config.categories && config.categories.length > 0;
  const hasExpressDelivery = !isExpressDeliveryCollection;
  const shouldShowDesktopFilter = hasFilterGroups;

  return (
    <>
      <div className={styles.desktopFilterContainer}>
        {hasExpressDelivery && (
          <ExpressDeliveryFilter
            isEnabled={state.is_express_delivery}
            onToggle={handleExpressDeliveryChange}
          />
        )}

        {shouldShowDesktopFilter && (
          <div className={styles.desktopFilter}>
            <div className={styles.header}>
              <Text className={styles.filterHeading}>{config.header.title}</Text>
            </div>

            <div className={styles.scrollContainer}>
              {hasFilterGroups && (
                <div className={styles.filterContent}>
                  <FilterGroups
                    filters={state}
                    onFilterChange={handleFilterChange}
                    onBudgetChange={handleBudgetChange}
                    openSections={openSections}
                    onToggleSection={handleToggleSection}
                  />
                </div>
              )}

              {!hasFilterGroups && hasExpressDelivery && (
                <div className={styles.filterContent}>
                  <div className={styles.noFiltersMessage}>
                    <Text className={styles.noFiltersText}>No additional filters available.</Text>
                  </div>
                </div>
              )}

              {(hasFilterGroups || (hasExpressDelivery && state.is_express_delivery)) && (
                <div className={styles.buttons}>
                  <Button
                    className={styles.clearButton}
                    onClick={handleClearAll}
                    disabled={!desktopFilterMetrics.hasAnyActiveFilters}
                  >
                    Clear all
                  </Button>
                  <Button
                    className={styles.applyButton}
                    onClick={handleApplyFilters}
                    disabled={!desktopFilterMetrics.hasAnyActiveFilters}
                  >
                    Apply
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {
        filterOptions && (
          <div className={styles.mobileFilter}>
            <FilterChips
              activeFilterType={activeFilterType}
              onFilterClick={handleMobileFilterClick}
              onFilterButtonClick={handleMobileFilterButtonClick}
              expressDeliveryChange={handleExpressDeliveryChange}
              expressDeliveryEnabled={state.is_express_delivery}
              hasFilterGroups={hasFilterGroups}
              variant={variant}
              cityName={cityName}
              analyticsPageType={pageName}
            />
          </div>
        )
      }

      <MobileFilterModal
        filters={state}
        isOpen={showMobileFilters}
        filterType={activeFilterType}
        onApply={handleMobileApplyFilters}
        onClose={handleCloseMobileFilter}
        onClear={handleMobileClearAll}
        onBudgetChange={handleBudgetChange}
        onExpressDeliveryChange={handleMobileExpressDeliveryChange}
        hasActiveFilters={mobileFilterMetrics.hasAnyActiveFilters}
        pageName={pageName}
        cityName={cityName}
        collectionName={collectionName}
        variant={variant}
      />
    </>
  );
});

FilterSection.displayName = 'FilterSection';

export default memo(FilterSection);